import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CargomaticDropzone from 'components/v2/DropZone';
import { createBulkShipments, getFileStatus, validateShipments } from 'utils/ShipmentWebAPIUtils';
import Container from 'components/v2/container';
import DeleteButton from '../../v2/buttons/DeleteButton';
import Papa from 'papaparse';
import moment from 'moment';
import DataPoller from '../../ui/DataPoller';
import Spinner from 'components/ui/Spinner';
import { StatsContainer, MetricCard} from 'components/v2/stats'
import { convertJsonToCSV } from 'utils/StreamHandlingUtils';

const dropzoneStyle = {
  display: 'flex',
  height: '50px',
  width: 'auto',
  border: '1px dashed black',
  borderRadius: '4px',
  cursor: 'pointer',
};

const DEFAULT_TEMPLATE = {
  DRIVER: 'Driver ID',
  PICKUP_START_DATE: 'Pickup Start Date',
  PICKUP_END_DATE: 'Pickup End Date',
  PICKUP_START_TIME: 'Pickup Start Time',
  PICKUP_END_TIME: 'Pickup End Time',
  DELIVERY_START_DATE: 'Delivery Start Date',
  DELIVERY_END_DATE: 'Delivery End Date',
  DELIVERY_START_TIME: 'Delivery Start Time',
  DELIVERY_END_TIME: 'Delivery End Time',
  LOAD_QUANTIY: 'Load Quantity',
  TRACKING: 'Tracking ID',
  MO_NUMBER: 'MO Number',
  MASTER_BILL_NUMBER: 'Bill Number',
  SHIPPER: 'Shipper ID',
  SHIPMENT_TYPE: 'Shipment Type',
  LOAD_TYPE: 'Load Type',
  LOAD_LENGTH: 'Load Length',
  LOAD_WIDTH: 'Load Width',
  LOAD_HEIGHT: 'Load Height',
  LOAD_WEIGHT: 'Load Weight',
  SHIPMENT_WEIGHT: 'Shipment Weight',
  PICKUP_LOCATION_NAME: 'Pickup Location Name',
  PICKUP_LOCATION: 'Pickup Location',
  DELIVERY_LOCATION_NAME: 'Delivery Location Name',
  DELIVERY_LOCATION: 'Delivery Location',
};

function generateTimeStamp (date, time) {
  const dateMoment = moment(date);
  const timeMoment = moment(time, 'hh:mm A');
  dateMoment.set({
    hour: timeMoment.get('hour'),
    minute: timeMoment.get('minute'),
    second: timeMoment.get('second')
  });
  return dateMoment.toISOString();
}

const BulkShipmentCreation = () => {
  const { t } = useTranslation();

  const [uploading, setUploading] = useState(false);
  const [showRows, setShowRows] = useState(false);

  const [columnHeaders, setColumnHeaders] = useState([]);
  const [fileData, setFileData] = useState();

  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState({});

  const [fileStatus, setFileStatus] = useState({_id: null});
  const [isBookingShipments, setIsBookingShipments]= useState(false);
  const [bookedShipments, setBookedShipments] = useState([]);
  const [failedShipments, setFailedShipments] = useState([]);
  const [uploadedData, setUploadedData] = useState([]);

  useEffect(() => {
    if (fileData) {
      validationErrors()
      prevalidateShipments()
    }
  }, [parsedData]);

  const results = [];

  const isValidDateFormat = (date) => {
    return /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date);
  };

  const isValidNumber = (number) => !Number.isNaN(number);

  const isValidTimeFormat = (time) => {
    return /((1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm]))/.test(time);
  };

  function renderDropZone() {
    if (fileData) {
      return null;
    }
    return (
      <CargomaticDropzone
        onDrop={onUpload}
        multiple={false}
        style={dropzoneStyle}
        dropzoneMessage={t('CLICK_DROP_FILE_TO_UPLOAD')}
        disabled={uploading}
      />
    );
  }

  async function onUpload(file) {
    try {
      setUploading(true);
      if (file) {
        setFileData(file[0]);
        csvToJson(file[0]);
        setUploading(false);
        setShowRows(true);
      }
    } catch (e) {
      setUploading(false);
    }
  }

  async function onRemove() {
    try {
      setShowRows(false);
      setFileData();
    } catch (e) {
      setFileData();
    }
  }

  async function csvToJson(file) {
    const onConversionComplete = (finalData) => {
      setColumnHeaders(finalData?.meta?.fields);
      const data = finalData.data.map((row) => {
        return {
            ...row,
            booking_id: Math.random().toString(20).slice(2, 10).toUpperCase()
           }
      })
      setParsedData(data);
    };
    const onReadError = (error) => {
      return error;
    };
    Papa.parse(file, {
      header: true,
      error: onReadError,
      complete: onConversionComplete,
    });
  }

  function renderCSVView() {
    return parsedData.map((results, i) => {
      const row = results;
      return mapRowData(row, i);
    });
  }

  function mapRowData(row, i) {
    const columns = Object.keys(DEFAULT_TEMPLATE).map((key) => {
      return <td>{row[DEFAULT_TEMPLATE[key]]}</td>;
    });
    if (errors[i]?.errors?.length) {
      columns.push(
        <td>
          <div>
            {errors[i].errors.map((error) => (
              <p>{error}</p>
            ))}
          </div>
        </td>,
      );
    }
    return <tr key={i}>{columns}</tr>;
  }

  function mapColumnHeaders() {
    return columnHeaders.map((header) => <th>{header}</th>);
  }

  function validationErrors() {
    const errors = {};
    parsedData.forEach((row, i) => {
      errors[i] = { errors: [] };
      Object.keys(row).forEach((column) => {
        switch (column) {
          case DEFAULT_TEMPLATE.PICKUP_START_DATE:
            if (!isValidDateFormat(row[column])) {
              errors[i].errors.push(t('PICKUP_START_DATE_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.PICKUP_END_DATE:
            if (!isValidDateFormat(row[column])) {
              errors[i].errors.push(t('PICKUP_END_DATE_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.DELIVERY_START_DATE:
            if (!isValidDateFormat(row[column])) {
              errors[i].errors.push(t('DELIVERY_START_DATE_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.DELIVERY_END_DATE:
            if (!isValidDateFormat(row[column])) {
              errors[i].errors.push(t('DELIVERY_END_DATE_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.PICKUP_START_TIME:
            if (!isValidTimeFormat(row[column])) {
              errors[i].errors.push(t('PICKUP_START_TIME_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.PICKUP_END_TIME:
            if (!isValidTimeFormat(row[column])) {
              errors[i].errors.push(t('PICKUP_END_TIME_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.DELIVERY_START_TIME:
            if (!isValidTimeFormat(row[column])) {
              errors[i].errors.push(t('DELIVERY_START_TIME_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.DELIVERY_END_TIME:
            if (!isValidTimeFormat(row[column])) {
              errors[i].errors.push(t('DELIVERY_END_TIME_INVALID'));
            }
            break;
          case DEFAULT_TEMPLATE.LOAD_HEIGHT:
            if (!isValidNumber(row[column])) {
              errors[i].errors.push(t('LOAD_HEIGHT_NOT_QTY'));
            }
            break;
          case DEFAULT_TEMPLATE.LOAD_LENGTH:
            if (!isValidNumber(row[column])) {
              errors[i].errors.push(t('LOAD_LENGTH_NOT_QTY'));
            }
            break;
          case DEFAULT_TEMPLATE.LOAD_QUANTIY:
            if (!isValidNumber(row[column])) {
              errors[i].errors.push(t('LOAD_QTY_NOT_QTY'));
            }
            break;
          case DEFAULT_TEMPLATE.LOAD_WEIGHT:
            if (!isValidNumber(row[column])) {
              errors[i].errors.push(t('LOAD_WEIGHT_NOT_QTY'));
            }
            break;
          case DEFAULT_TEMPLATE.LOAD_WIDTH:
            if (!isValidNumber(row[column])) {
              errors[i].errors.push(t('LOAD_WIDTH_NOT_QTY'));
            }
            break;
          default:
            break;
        }
      });
    });
    return errors;
  }

  function mapPayloadData() {
    const data = parsedData.map((row) => {
      return {
        boxes: [],
        pallets: [],
        payables: [],
        receivables: [],
        reference_numbers: [],
        shipment_type: 'dayrate',
        shipper: row['Shipper ID'],
        weight: row['Shipment Weight'],
        project: false,
        mo_number: row['MO Number'],
        booking_id: row.booking_id,
        loads: [
          {
            type: row['Load Type'].toLowerCase(),
            quantity: row['Load Quantity'],
            length: row['Load Length'],
            width: row['Load Width'],
            height: row['Load Height'],
            weight: row['Load Weight'],
            weight_units: "lbs"
          }
        ],
        stops: [
          {
            action: "pickup",
            empty: false,
            sequence: 1,
            window_start: generateTimeStamp(row['Pickup Start Date'], row['Pickup Start Time']),
            window_end: generateTimeStamp(row['Pickup End Date'], row['Pickup End Time']),
            appointment: false,
            appointment_unconfirmed: true,
            equipments: [],
            location: [
            {
                name: row['Pickup Location Name'],
                address: row['Pickup Location'],
                market: 'LAX'
            }
            ],
            contacts: [],
            reference_numbers: []
            },
            {
              action: "deliver",
              empty: false,
              sequence: 2,
              window_start: generateTimeStamp(row['Delivery Start Date'], row['Delivery Start Time']),
              window_end: generateTimeStamp(row['Delivery End Date'], row['Delivery End Time']),
              appointment: false,
              appointment_unconfirmed: true,
              equipments: [],
              location: [
              {
                  name: row['Delivery Location Name'],
                  address: row['Delivery Location'],
                  market: 'LAX'
              }
              ],
              contacts: [],
              reference_numbers: [],
              }
        ]
      }
    })
    return data
  }

  function mapPreValidationPayloadData() {
    const data = parsedData.map((row) => {
      return {
        boxes: [],
        pallets: [],
        payables: [],
        receivables: [],
        reference_numbers: [],
        shipment_type: 'dayrate',
        shipper: row['Shipper ID'],
        weight: row['Shipment Weight'],
        project: false,
        mo_number: row['MO Number'],
        booking_id: row.booking_id,
        loads: [
          {
            type: row['Load Type'].toLowerCase(),
            quantity: row['Load Quantity'],
            length: row['Load Length'],
            width: row['Load Width'],
            height: row['Load Height'],
            weight: row['Load Weight'],
            weight_units: "lbs"
          }
        ],
        stops: [
          {
            action: "pickup",
            empty: false,
            sequence: 1,
            startTime: row['Pickup Start Time'],
            startDate: row['Pickup Start Date'],
            endTime: row['Pickup End Time'],
            endDate: row['Pickup End Date'],
            appointment: false,
            appointment_unconfirmed: true,
            equipments: [],
            location: [
            {
                name: row['Pickup Location Name'],
                address: row['Pickup Location'],
                market: 'LAX'
            }
            ],
            contacts: [],
            reference_numbers: []
            },
            {
              action: "deliver",
              empty: false,
              sequence: 2,
              startTime: row['Delivery Start Time'],
              startDate: row['Delivery Start Date'],
              endTime: row['Delivery End Time'],
              endDate: row['Delivery End Date'],
              appointment: false,
              appointment_unconfirmed: true,
              equipments: [],
              location: [
              {
                  name: row['Delivery Location Name'],
                  address: row['Delivery Location'],
                  market: 'LAX'
              }
              ],
              contacts: [],
              reference_numbers: [],
              }
        ]
      }
    })
    return data
  }

  async function prevalidateShipments () {
    try {
      const shipments_to_book = mapPreValidationPayloadData();
      const response = await validateShipments({shipments_to_book});
      const {result} = response.entity;
      setFileStatus(result);
    } catch(e) {
      throw e;
    }
  }

  async function createShipments () {
    try {
      const shipments_to_book = mapPayloadData();
      setIsBookingShipments(true);
      await createBulkShipments({shipments_to_book, fileStatusId: fileStatus._id})
    } catch(e) {
      setIsBookingShipments(false);
      throw e;
    }
  }

  async function getFileStatusResults () {
    try {
      if (!fileStatus) {
        return;
      }
      const { _id, count } = fileStatus;
      const shipmentRequestTotal = bookedShipments.length + failedShipments.length;
      if ((bookedShipments.length + failedShipments.length) < count) {
        const response = await getFileStatus(_id);
        const { result: { booked_shipments: updatedBookedShipments, failed_shipments: updatedFailedShipments } } = response.entity
        if (updatedBookedShipments.length > bookedShipments.length) {
          setBookedShipments(updatedBookedShipments);
        }
        if (updatedFailedShipments.length > failedShipments.length) {
          setFailedShipments(updatedFailedShipments);
        }
      }
      if (shipmentRequestTotal >= count && isBookingShipments) {
        setIsBookingShipments(false);
        setUploadedData(parsedData);
        setFileData(null);
        setFileStatus((currentState) => { return {...currentState, ...fileStatus, bookingComplete: true } })
      }
      return fileStatus
    } catch (e) {
      throw e;
    }
  }

  function resetShipmentCreation () {
    setIsBookingShipments(false);
    setFileData(null);
    setFileStatus({_id: null});
    setBookedShipments([]);
    setFailedShipments([]);
  }

  async function generateCSV () {
    const response = await getFileStatus(fileStatus._id);
    const {result} = response.entity
    try {
      const csvData = uploadedData.map((row) => {
        const shipmentBooked = result.booked_shipments.find((shipment) => shipment.booking_id === row.booking_id);
        if (shipmentBooked) {
          return {
            ...row,
            'Shipment ID/Failure Messages': shipmentBooked.shipment_id
          }
        }
        const shipmentFailed = result.failed_shipments.find((shipment) => shipment.booking_id === row.booking_id);
        if (shipmentFailed) {
          return {
            ...row,
            'Shipment ID/Failure Messages': shipmentFailed.shipmentErrors.join(',')
          }
        }
        return row;
      });
      if (csvData.length) {
        convertJsonToCSV(csvData, `bulk_booked_shipments_${new Date().toISOString}.csv`)
      }
    } catch (e) {
      throw e;
    }
  }

  function redirectToShipmentsList () {
    window.location.href = `/shipments?bulk_booking_id=${fileStatus._id}`;
  }

  return (
    <Container className="py-4">
      <h3 className="text-lg font-medium text-gray-900 leading-6 py-4">
        {t('BULK_SHIPMENT_CREATION')}
        <p>{t('CREATE_BULK_SHIPMENT_TOOL')}</p>
      </h3>
      <DataPoller action={getFileStatusResults} frequency={5000} />
      {isBookingShipments && <Spinner showLarge showMessage={false} />}
      {showRows && !isBookingShipments && fileData &&(
        <div>
          <div className="flex">
            <h5 className="pr-2">{fileData?.name}</h5>
            <div className="flex justify-between">
              <DeleteButton className="my-auto" onClick={onRemove} />
              <button className="button button-green h-10 ml-2 my-auto" onClick={createShipments} >
                {t('CREATE_SHIPMENTS')}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="default-table">
              <thead>
                <tr>{mapColumnHeaders()}</tr>
              </thead>
              <tbody>{renderCSVView()}</tbody>
            </table>
          </div>
        </div>
      )}
      {!fileData && !fileStatus.bookingComplete && (
        <div className="max-w-md p-6 bg-white rounded shadow">
          {uploading ? (
            <div className="flex">
              <fa className="fa fa-spinner fa-spin text-green m-auto text-5xl" />
            </div>
          ) : (
            renderDropZone()
          )}
        </div>
      )}
      {(bookedShipments.length > 0 || failedShipments.length > 0) && (
        <div>
          <h3>{fileStatus.bookingComplete ? 'Your shipments have been successfully created!' : 'Your shipments are being created.'}</h3>
          <StatsContainer>
            <MetricCard
              id="total"
              label="Total Shipments"
              type="number"
              getMetric={() => new Promise((resolve) => resolve({ value: fileStatus.count }))}
            />
            <MetricCard
              id="created"
              label="Shipments Created"
              type="number"
              getMetric={() => new Promise((resolve) => resolve({ value: bookedShipments.length }))}
              params={bookedShipments.length}
            />
            <MetricCard
              id="failed"
              label="Shipments Failed"
              type="number"
              getMetric={() => new Promise((resolve) => resolve({ value: failedShipments.length }))}
              params={failedShipments.length}
            />
          </StatsContainer>
          <div className="flex my-4">
            <button className='button button-green w-64 my-auto mx-2 h-10' onClick={redirectToShipmentsList}>View Your Shipments</button>
            <button className='button button-green w-64 my-auto h-10' onClick={resetShipmentCreation} >Upload New Shipments</button>
            <button className='button button-green w-64 my-auto h-10 ml-2' onClick={generateCSV} >Download CSV</button>
          </div>
        </div>
      )}
    </Container>
  );
}

export default BulkShipmentCreation;
